import { MOVEMENT_CONFIG } from '@common/config';
import { PlayerUpdateSystemArgs } from '@common/systems/SystemBase';

import { ClientWorld } from '../ClientWorld';
import { ClientRegistry, FacingDirection } from '../components';
import { ClientPlayerUpdateSystem } from './ClientBase';

/**
 * Periodically sends the local player's authoritative position, velocity, and facing
 * to the server for validation and broadcasting to other clients.
 *
 * Runs at a fixed interval defined in MOVEMENT_CONFIG.SYNC_INTERVAL_MS (default 20Hz).
 */
export class ClientPositionSyncSystem extends ClientPlayerUpdateSystem {
  private lastSyncTime = 0;
  private lastSentX = 0;
  private lastSentY = 0;
  private lastSentDx = 0;
  private lastSentDy = 0;
  private lastSentFacing: FacingDirection | undefined = undefined;
  private lastHeartbeatTime = 0;
  private readonly HEARTBEAT_INTERVAL_MS = 5000;

  updatePlayer({ entityId, world }: PlayerUpdateSystemArgs<ClientRegistry, ClientWorld>): void {
    const now = performance.now();
    if (now - this.lastSyncTime < MOVEMENT_CONFIG.SYNC_INTERVAL_MS) {
      return;
    }

    const transform = world.getComponent(entityId, 'Transform');
    const velocity = world.getComponent(entityId, 'Velocity');
    const facing = world.getComponent(entityId, 'Facing');

    // Basic requirements for a valid move payload
    if (!transform || !velocity || !facing) {
      return;
    }

    // Check for movement, rotation, or velocity changes since last sync
    const hasMoved =
      Math.abs(transform.x - this.lastSentX) > 0.1 || Math.abs(transform.y - this.lastSentY) > 0.1;
    const hasRotated = facing.direction !== this.lastSentFacing;
    const hasChangedVelocity =
      Math.abs(velocity.dx - this.lastSentDx) > 0.01 ||
      Math.abs(velocity.dy - this.lastSentDy) > 0.01;
    const isHeartbeat = now - this.lastHeartbeatTime > this.HEARTBEAT_INTERVAL_MS;

    if (!hasMoved && !hasRotated && !hasChangedVelocity && !isHeartbeat) {
      return;
    }

    this.clientServices.connection.send({
      playerId: this.clientServices.localPlayer.playerId,
      payload: [
        {
          type: 'playerMove',
          x: transform.x,
          y: transform.y,
          dx: velocity.dx,
          dy: velocity.dy,
          facing: facing.direction,
          timestamp: now,
        },
      ],
    });

    this.lastSyncTime = now;
    this.lastSentX = transform.x;
    this.lastSentY = transform.y;
    this.lastSentDx = velocity.dx;
    this.lastSentDy = velocity.dy;
    this.lastSentFacing = facing.direction;
    this.lastHeartbeatTime = now;
  }
}
